import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Loader2, Phone, ArrowLeft, Shield, CheckCircle, Lock } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const UstaLogin = () => {
  const navigate = useNavigate();
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/usta/login-password`, { 
        phone_number: phone,
        password: password 
      });
      localStorage.setItem('usta_token', response.data.token);
      localStorage.setItem('usta_data', JSON.stringify(response.data.user));
      navigate('/prousta/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Giriş yapılamadı. Telefon veya şifre hatalı.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] flex">
      {/* Sol Panel - Bilgi */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-[#006D77] to-[#004d54] p-12 flex-col justify-between">
        <div>
          <img src="/logo-white.png" alt="HizmetGeliyo" className="h-12 mb-12" />
          <h1 className="text-4xl font-bold text-white mb-4">
            Usta Portalı
          </h1>
          <p className="text-white/80 text-lg mb-8">
            Müşterilerinize profesyonel hizmet sunun, işlerinizi kolayca yönetin.
          </p>
          
          <div className="space-y-4">
            <div className="flex items-center gap-3 text-white/90">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <CheckCircle className="w-5 h-5" />
              </div>
              <span>Yeni iş fırsatları alın</span>
            </div>
            <div className="flex items-center gap-3 text-white/90">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <Shield className="w-5 h-5" />
              </div>
              <span>Güvenli ödeme sistemi</span>
            </div>
            <div className="flex items-center gap-3 text-white/90">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <Phone className="w-5 h-5" />
              </div>
              <span>7/24 destek hattı</span>
            </div>
          </div>
        </div>
        
        <p className="text-white/60 text-sm">
          © 2026 HizmetGeliyo. Tüm hakları saklıdır.
        </p>
      </div>

      {/* Sağ Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          {/* Mobil Logo */}
          <div className="lg:hidden flex justify-center mb-8">
            <img src="/logo-transparent.png" alt="HizmetGeliyo" className="h-16" />
          </div>

          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Giriş Yap</h2>
            <p className="text-gray-500 mb-6">Telefon numarası ve şifrenizle giriş yapın</p>

            <form onSubmit={handleLogin} className="space-y-5">
              <div>
                <Label htmlFor="phone" className="text-sm font-medium text-gray-700">
                  Telefon Numarası
                </Label>
                <div className="relative mt-2">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <span className="absolute left-10 top-1/2 -translate-y-1/2 text-gray-700 font-medium">0</span>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="(5XX) XXX XX XX"
                    value={(() => {
                      const digits = phone.replace(/\D/g, '').slice(1); // Remove leading 0
                      if (digits.length === 0) return '';
                      if (digits.length <= 3) return `(${digits}`;
                      if (digits.length <= 6) return `(${digits.slice(0,3)}) ${digits.slice(3)}`;
                      if (digits.length <= 8) return `(${digits.slice(0,3)}) ${digits.slice(3,6)} ${digits.slice(6)}`;
                      return `(${digits.slice(0,3)}) ${digits.slice(3,6)} ${digits.slice(6,8)} ${digits.slice(8,10)}`;
                    })()}
                    onChange={(e) => {
                      let value = e.target.value.replace(/\D/g, '');
                      if (value.length <= 10) setPhone('0' + value);
                    }}
                    className="pl-14 h-12 text-base border-gray-200 focus:border-[#006D77] focus:ring-[#006D77] font-mono tracking-wide"
                    required
                    maxLength={17}
                    data-testid="usta-phone"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="password" className="text-sm font-medium text-gray-700">
                  Şifre
                </Label>
                <div className="relative mt-2">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 h-12 text-base border-gray-200 focus:border-[#006D77] focus:ring-[#006D77]"
                    required
                    data-testid="usta-password"
                  />
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-600 text-sm">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={loading || !phone || !password}
                className="w-full h-12 bg-[#006D77] hover:bg-[#005a63] text-white font-medium"
                data-testid="usta-login-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : 'Giriş Yap'}
              </Button>
            </form>

            <div className="mt-6 pt-6 border-t border-gray-100 text-center space-y-3">
              <Link 
                to="/prousta/register" 
                className="text-[#006D77] hover:underline text-sm font-medium block"
              >
                Henüz kayıt olmadınız mı? Kayıt Ol
              </Link>
              <button
                onClick={() => navigate('/')}
                className="text-gray-500 hover:text-gray-700 text-sm flex items-center justify-center gap-1 mx-auto"
              >
                <ArrowLeft className="w-4 h-4" /> Ana Sayfaya Dön
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UstaLogin;
